import useEnv from '../composables/useEnv'
import useRestNode from '../composables/useRestNode'
import usePlan from '../composables/usePlan'
import useSort from '../composables/useSort'
import useLogger from '../composables/useLoggerLite'

export default () => {

    const { locationServiceEndpoint } = useEnv()
    const { get } = useRestNode()
    const { withTimings } = useLogger()


    /**
     * Executes a location search.
     * The function takes as a parameter an object ( using the options pattern ) that contains the following parameters:
     * 1)  profile - The findcare profile
     * 2)  context - The context of the application as managed in the findcare applications
     * 3)  careConcepts - The careConcepts to apply to the search
     * 4)  betterChoiceContext - The betterChoiceContext to apply toward the search.
     * 5)  recordCount - The number of practitioners the service should return
     * 6)  decorators - An array of functions that can be used to apply additional attribute to each search result.  The decorator functions
     *     are supplied by the higher order function which allows the higher order function to add crosscutting behavior to location and practitioner
     *     searches while also having the benefit of executing them in the lower order search and thus gaining the benefit of being part of the 
     *     Promise.all and not having to be executed after the fact. 
     */
    const searchLocations = async ({searchId, profile, context, careConcepts, betterChoiceContext, recordCount = 300, decorators = [] }) => {

        if ( !profile ) throw new Error("Profile must be pass to the searchLocations service.")
        if ( !context ) throw new Error("Context must be passed to the searchLocations service.")

        const { getNetworkId, getGroupNumber } = usePlan(profile, context)
        const { getCpnSortString } = useSort(profile, context)
        const { type, latLong, networkId, name, distance, asOfDate, acceptingNewPatients, tier, term, careNetworkId, pcOnly } = context
    
        try {
          let contextualString = '/search-medical-locations'
          if (context.type === 'dental') contextualString = '/search-dental-locations'
          if (context.type === 'urgent') contextualString = '/search-urgent-care-locations'
          if (context.type.includes('hospital')) contextualString = '/search-hospital-locations'
    
          const url = new URL(`${locationServiceEndpoint}${contextualString}/network/${getNetworkId(profile, context)}`)
          url.searchParams.append('recordCount', recordCount)

          if (context.sort) url.searchParams.append('sortBy', getCpnSortString(context.sort))
          if (betterChoiceContext) url.searchParams.append('betterChoiceContext', betterChoiceContext)
          if (latLong) {
            url.searchParams.append('latitude', latLong.split(',')[0])
            url.searchParams.append('longitude', latLong.split(',')[1])
          }
          if (getGroupNumber()) url.searchParams.append('employerGroupId', getGroupNumber())
          if (name) url.searchParams.append('name', name)
          if (distance) url.searchParams.append('distance', distance)
          if (asOfDate) url.searchParams.append('asof', asOfDate)
          if (acceptingNewPatients) url.searchParams.append('acceptingNewPatients', acceptingNewPatients)
          if (pcOnly) url.searchParams.append('pcOnly', pcOnly)
    
          if (careNetworkId && type !== 'dental') url.searchParams.append('careNetworkId', careNetworkId)
          if (tier) url.searchParams.append('tier', tier)
          //We don't apply care concepts to hospitals only clinics.
          if (term && !context.type.includes('hospital')) {
            //const { careConcepts } = await getCareConceptsForSearch()
    
            //If there are no concepts available for the term return no results.
            if (careConcepts && careConcepts.length > 0) {
              url.searchParams.append('careConcepts', careConcepts.join())
            } else return []
          }
    
          //For hospitals, apply the hospital subtype
          //The tyep will be in the form of hospital:subtype
          // Hospitals - hospital:1
          // Outpatient - hospital:2
          // Skilled Nursing - hospital:3
          // Specialty care = hospital  ( Note this is a special case without a subtype that includes all )
          if (context.type.includes('hospital')) {
            const parts = context.type.split(':')
    
            //If we don't have a subtype, then don't include the subtype param.  Specialty Care is the special
            //case that doesn't have a subtype and should include all of the hospital types.
            if (parts.length > 1) {
              url.searchParams.append('hospSubtype', parts[1])
            }
          }
    
          console.log('searching with URL:', url.toString())
          // let result = await get(url.toString())
          const result = await withTimings(`searchLocations (${searchId})`,get, url.toString())

          //Decorate the search results with additional params using decorator functions supplied by the 
          //Higher order useSearchService function.
          decorate(result.value.locations, decorators)
          
          return result.value.locations
        } catch (error) {
          console.log(error)
          return []
        }
      }

      /**
       * Decorate each location by applying each decorator function supplied by the higher order function
       * to eacch result.
       */
      const decorate = (results, decoratorFunctions) => {

        results.forEach(item => {
            decoratorFunctions.forEach(f => f(item))
        })
      }      

      return { searchLocations, decorate }

}